import React from 'react';

/**
 * LineupQuickActions - Show players with quick action buttons
 * Active players at top, substituted in middle, expelled at bottom (all grayed out)
 */
export const LineupQuickActions = React.memo(({ matchId, homeTeam, awayTeam, activeCounts, onEventCreated, teamFilter, columns }) => {
  
  const renderPlayer = (playerPos) => {
    const { player, eligibility, is_captain, jersey_number } = playerPos;
    const isExpelled = eligibility.isExpelled;
    const isSubstituted = eligibility.isSubstituted;
    const isInactive = !eligibility.eligible;

    return (
      <div 
        key={playerPos.id}
        className={`p-3 rounded-lg border-2 flex items-center justify-between ${
          isInactive 
            ? 'bg-gray-100 border-gray-300 opacity-60' 
            : 'bg-white border-gray-200 hover:border-blue-300'
        }`}
      >
        {/* Player Info */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className={`text-xl font-bold flex-shrink-0 ${isInactive ? 'text-gray-400' : 'text-gray-700'}`}>
            #{jersey_number}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              {is_captain && (
                <span className="px-1 py-0.5 bg-yellow-400 text-yellow-900 text-[10px] font-bold rounded flex-shrink-0">
                  C
                </span>
              )}
              <span className={`font-semibold truncate ${isInactive ? 'text-gray-500' : 'text-gray-800'}`}>
                {player.first_name} {player.last_name}
              </span>
            </div>
            
            {/* Status badges */}
            {isExpelled && (
              <span className="text-xs text-red-600 font-bold">
                SENT OFF - {eligibility.expulsionReason === 'red_card' ? 'Red card' : '2 yellow cards'}
              </span>
            )}
            {isSubstituted && (
              <span className="text-xs text-blue-600 font-bold">
                SUBBED OUT
              </span>
            )}
            {eligibility.yellowCards === 1 && !isExpelled && !isSubstituted && (
              <span className="text-xs text-yellow-600 font-bold">
                ⚠ 1 yellow card
              </span>
            )}
          </div>
        </div>

        {/* Quick Action Buttons */}
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={() => onEventCreated('goal', playerPos)}
            disabled={isInactive}
            className="w-10 h-10 rounded-lg bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center text-xl"
            title="Goal"
          >
            ⚽
          </button>
          <button
            onClick={() => onEventCreated('yellow_card', playerPos)}
            disabled={isInactive}
            className="w-10 h-10 rounded-lg bg-yellow-400 hover:bg-yellow-500 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center text-xl"
            title={eligibility.yellowCards === 1 ? "Yellow Card (Warning: Already has 1)" : "Yellow Card"}
          >
            <span className="inline-block w-6 text-center scale-90">
              {eligibility.yellowCards === 1 ? '🟨🟨' : '🟨'}
            </span>
          </button>
          <button
            onClick={() => onEventCreated('red_card', playerPos)}
            disabled={isInactive}
            className="w-10 h-10 rounded-lg bg-red-500 hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center text-xl"
            title="Red Card"
          >
            🟥
          </button>
          <button
            onClick={() => onEventCreated('substitution', playerPos)}
            disabled={isInactive}
            className="w-10 h-10 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center text-xl"
            title="Substitution"
          >
            🔄
          </button>
        </div>
      </div>
    );
  };

  const renderTeam = (players, teamColor, teamLabel, counts) => {
    // Show all active players (original starters + substitutes who entered)
    const activePlayers = players.filter(p => 
      p.eligibility.eligible && 
      !p.eligibility.isSubstituted && 
      !p.eligibility.isExpelled
    );
    // Only original starters can be substituted/expelled
    const substitutedPlayers = players.filter(p => p.is_starting && p.eligibility.isSubstituted);
    const expelledPlayers = players.filter(p => p.is_starting && p.eligibility.isExpelled);

    return (
      <div>
        <div className={`flex items-center justify-between mb-3 pb-2 border-b-2 border-${teamColor}-200`}>
          <h3 className={`text-lg font-bold text-${teamColor}-600`}>
            {teamLabel}
          </h3>
          {counts && (
            <span className={`text-lg font-bold text-${teamColor}-600`}>
              {counts.active}/{counts.total}
            </span>
          )}
        </div>
        
        {/* Active Players */}
        <div className="space-y-2 mb-4">
          {activePlayers.length === 0 ? (
            <p className="text-center text-gray-400 py-4">No active players</p>
          ) : (
            activePlayers.map(renderPlayer)
          )}
        </div>

        {/* Substituted Players */}
        {substitutedPlayers.length > 0 && (
          <>
            <div className="border-t-2 border-dashed border-blue-200 pt-3 mb-2">
              <h4 className="text-xs font-bold text-blue-500 uppercase mb-2">Substituted</h4>
            </div>
            <div className="space-y-2 mb-4">
              {substitutedPlayers.map(renderPlayer)}
            </div>
          </>
        )}

        {/* Expelled Players */}
        {expelledPlayers.length > 0 && (
          <>
            <div className="border-t-2 border-dashed border-red-200 pt-3 mb-2">
              <h4 className="text-xs font-bold text-red-500 uppercase mb-2">Expelled</h4>
            </div>
            <div className="space-y-2">
              {expelledPlayers.map(renderPlayer)}
            </div>
          </>
        )}
      </div>
    );
  };

  const gridCols = columns === 1 ? 'grid-cols-1' : 'grid-cols-2';
  const showHome = !teamFilter || teamFilter === 'home';
  const showAway = !teamFilter || teamFilter === 'away';

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold mb-4">Quick Actions</h2>

      <div className={`grid ${gridCols} gap-6`}>
        {/* Home Team */}
        {showHome && renderTeam(homeTeam, 'blue', 'HOME TEAM', activeCounts?.homeTeam)}

        {/* Away Team */}
        {showAway && renderTeam(awayTeam, 'green', 'AWAY TEAM', activeCounts?.awayTeam)}
      </div>
    </div>
  );
});
